import { useEffect, useState } from 'react';
import { useT } from '../i18n/index.js';
import { actualizarAhora, revisarVersion } from '../lib/actualizacion.js';
import { Ico } from './Iconos.js';

/** Under this a check says nothing: the door just opens a moment later. */
const CALLADO_MS = 400;

export type EstadoRevision = 'revisando' | 'nueva' | 'actualizando' | 'fallo';

export interface Revision {
  estado: EstadoRevision;
  /** Long enough that the door says what it is waiting for. */
  tarda: boolean;
  actualizar: () => void;
}

/**
 * The check behind the door (`Puerta.tsx`, `lib/actualizacion.ts`), from the moment `activa`
 * turns on: the PIN was right, or there was none to ask for. Up to date, or the server out of
 * reach, it calls `onAlDia` and the door opens; a new version keeps it shut. Turned off again,
 * the PIN pad back over a check that had not finished, it forgets it, and the next PIN asks anew.
 */
export function useRevision(activa: boolean, onAlDia: () => void): Revision {
  const [estado, setEstado] = useState<EstadoRevision>('revisando');
  const [tarda, setTarda] = useState(false);

  useEffect(() => {
    if (!activa) {
      setEstado('revisando');
      setTarda(false);
      return;
    }
    let vivo = true;
    const reloj = window.setTimeout(() => setTarda(true), CALLADO_MS);
    void revisarVersion().then((r) => {
      window.clearTimeout(reloj);
      if (!vivo) return;
      if (r === 'nueva') setEstado('nueva');
      else onAlDia();
    });
    return () => {
      vivo = false;
      window.clearTimeout(reloj);
    };
    // Each time it turns on, not each time the door hands it a new `onAlDia`.
  }, [activa]);

  return {
    estado,
    tarda,
    actualizar: () => {
      setEstado('actualizando');
      actualizarAhora().catch(() => setEstado('fallo'));
    },
  };
}

/**
 * A new version, with «Actualizar» the only way on (the owner, 2026-10-09). It says that nothing
 * saved is lost by it, sent or not, since that is what anybody pressing it in the middle of a
 * shift wants to know first.
 *
 * Should it fail, the connection gone halfway through say, the till cannot be left shut: it says
 * so and offers to try again, and only then to go on with the old version, as a choice somebody
 * makes after trying rather than a button beside the one they should press.
 */
export function VersionNueva({
  estado,
  onActualizar,
  onSeguir,
}: {
  estado: Exclude<EstadoRevision, 'revisando'>;
  onActualizar: () => void;
  onSeguir: () => void;
}) {
  const t = useT();
  const actualizando = estado === 'actualizando';
  return (
    <div
      className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-5 p-6 text-center"
      data-version-nueva={estado}
    >
      <span className="chip chip-marca size-16 rounded-2xl">
        <Ico icono="actualizar" className={`size-8 ${actualizando ? 'motion-safe:animate-spin' : ''}`} />
      </span>
      <h1 className="text-2xl font-bold">{t('actualizar.titulo')}</h1>
      <p className="text-sm text-slate-400">{t('actualizar.hayQue')}</p>
      {estado === 'fallo' && (
        <p role="alert" className="rounded-xl bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          {t('actualizar.fallo')}
        </p>
      )}
      <div className="flex w-full flex-col gap-3">
        <button type="button" className="btn-primario w-full" disabled={actualizando} onClick={onActualizar}>
          <Ico icono="actualizar" />
          {t(actualizando ? 'actualizar.actualizando' : estado === 'fallo' ? 'actualizar.reintentar' : 'actualizar.ahora')}
        </button>
        {estado === 'fallo' && (
          <button type="button" className="btn-secundario w-full" onClick={onSeguir}>
            <Ico icono="adelante" />
            {t('actualizar.seguirSinActualizar')}
          </button>
        )}
      </div>
    </div>
  );
}
